import React, { useState, useRef, useEffect } from 'react';
import { Popover, Button } from 'react-bootstrap';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useIntl } from 'react-intl';

import SimplePrompt from 'components/layout/SimplePrompt';
import CmsNewElement from 'components/cms/CmsNewElement';
import OverlayTrigger from 'components/OverlayTrigger';
import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';


const CmsElementCol = (props) => {
  const {
    editMode,
    canvasContainerRef,
    index, // z.B. "0.0"
    children,
    scale,
    showElementSettings,
    removeElement,
    addElement,
  } = props;

  const { messages } = useIntl();
  const [mouseOver, setMouseOver] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [colMenuMarginLeft, setColMenuMarginLeft] = useState(0);

  const containerRef = useRef(null);
  const overlayRef = useRef(null);

  // CMS Logic Hook
  const {
    shown,
    setShown,
    getMainId,
    getCustomId,
    getParamValue,
    getResponsiveClasses,
    getPaddingClasses,
    getBackgroundStyle,
    elementStyleTag,
    getParallaxData,
    getAppendHtml,
    cssClasses,
  } = useAbstractCmsElement(props);

  /**
   * dnd-kit: Droppable Hook
   * So that the column is recognized as the target when it is empty.
   */
  const { setNodeRef, isOver } = useDroppable({
    id: index,
    disabled: !editMode,
  });

  /**
   * dnd-kit: Sortable Context IDs
   * We need to collect the IDs of the children (0.0.0, 0.0.1 etc.)
   * so that the context knows which elements to manage.
   */
    // In CmsElementCol.js
  const childIds = React.Children.map(children, (child) => {
      return child.props.id || child.props.index;
    }) || [];

  // Menu shift (prevents overlap with the row menu)
  useEffect(() => {
    if (editMode && mouseOver && containerRef.current) {
      const colMenu = containerRef.current.querySelector('.ms-col-menu');
      if (colMenu) {
        const colMenuRect = colMenu.getBoundingClientRect();
        let rowContainer = containerRef.current;
        while (rowContainer.parentElement && !rowContainer.classList.contains('ms-row')) {
          rowContainer = rowContainer.parentElement;
        }

        if (rowContainer && rowContainer.classList.contains('ms-row')) {
          const rowMenu = rowContainer.querySelector('.ms-row-menu');
          if (rowMenu) {
            const rowMenuRect = rowMenu.getBoundingClientRect();
            if (rowMenuRect.left + rowMenuRect.width > colMenuRect.left &&
              rowMenuRect.top + rowMenuRect.height > colMenuRect.top) {
              const overlap = rowMenuRect.width - (colMenuRect.left - rowMenuRect.left) + 10;
              setColMenuMarginLeft(overlap * (1 / scale));
            } else {
              setColMenuMarginLeft(0);
            }
          }
        }
      }
    } else {
      setColMenuMarginLeft(0);
    }
  }, [mouseOver, editMode, scale]);

  const getResponsiveDef = () => {
    const xs = parseInt(getParamValue('xs', 12), 10);
    const xsOffset = parseInt(getParamValue('xsOffset', 0), 10);
    const sm = parseInt(getParamValue('sm', xs), 10);
    const smOffset = parseInt(getParamValue('smOffset', 0), 10);
    const md = parseInt(getParamValue('md', sm), 10);
    const mdOffset = parseInt(getParamValue('mdOffset', 0), 10);
    const lg = parseInt(getParamValue('lg', md), 10);
    const lgOffset = parseInt(getParamValue('lgOffset', 0), 10);

    return ` col-xs-${xs} col-sm-${sm} col-md-${md} col-lg-${lg} col-xs-offset-${xsOffset} col-sm-offset-${smOffset} col-md-offset-${mdOffset} col-lg-offset-${lgOffset}`;
  };

  const parallaxData = getParallaxData();

  const innerContent = (
    <div
      ref={(node) => {
        containerRef.current = node;
        setNodeRef(node); // Important for useDroppable
      }}
      className={`col ms-col ${shown ? 'elem-shown ' : 'elem-hidden '} ${getResponsiveDef()} ${getResponsiveClasses()} ${cssClasses} ${isOver ? 'ms-col-drop-active' : ''}`}
      style={{
        ...getBackgroundStyle(),
        outline: isOver && editMode ? '2px dashed #28a745' : 'none',
        minHeight: editMode && !children ? '80px' : 'auto' // Makes dropping into empty columns easier.
      }}
      onMouseEnter={() => editMode && setMouseOver(true)}
      onMouseLeave={() => editMode && setMouseOver(false)}
    >
      {elementStyleTag}

      {/* Column menu (Settings & Delete) */}
      {(mouseOver || showNew) && editMode && (
        <>
          <span className="ms-col-outline ms-col-outline-top" />
          <span className="ms-col-outline ms-col-outline-right" />
          <span className="ms-col-outline ms-col-outline-bottom" />
          <span className="ms-col-outline ms-col-outline-left" />
          <div
            className="ms-col-menu"
            style={{
              transform: `scale(${1 / scale})`,
              transformOrigin: 'top left',
              marginLeft: `${colMenuMarginLeft}px`,
            }}
          >
            <Button onClick={(e) => showElementSettings(e, index)}>
              <i className="fal fa-cog" />
            </Button>
            <SimplePrompt
              onSuccess={() => removeElement(index)}
              placement="bottom"
              variant="default"
              showLabel={false}
            />
          </div>
        </>
      )}

      {/*
          HERE is the crucial context for the children (images, text, etc.).
          Without this context, the children know nothing about each other.
      */}
      <SortableContext items={childIds} strategy={verticalListSortingStrategy}>
        {React.Children.map(children, (child, i) => {
          if (!child) return null;
          return React.cloneElement(child, {
            index: `${index}.${i}`
          });
        })}
      </SortableContext>

      {/* Placeholder icon if empty */}
      {!children && (
        <div
          className="p-t-15 p-b-15 text-center"
          onClick={() => overlayRef.current?.triggerVisible()}
          style={{ opacity: 0.2, cursor: 'pointer' }}
        >
          <i className="fa fa-cube fa-4x" />
        </div>
      )}

      {/* Button to add new items */}
      {editMode && (
        <div className="text-center ms-add-new ms-add-new-element">
          <OverlayTrigger
            ref={overlayRef}
            container={() => canvasContainerRef.current}
            delayHide={150}
            placement="bottom"
            trigger="click"
            rootClose
            wrappingComponent={Popover}
            id={`add_element_${getMainId()}`}
            overlay={<CmsNewElement addElement={(el) => addElement(index, el)} />}
            onEnter={() => setShowNew(true)}
            onExit={() => setShowNew(false)}
          >
            <a
              className={`ms-cms-button btn btn-default btn-rounded ${(mouseOver || showNew) ? 'btn-show' : 'btn-hide'}`}
              style={{
                transform: `scale(${1 / scale}) translateY(${15 * scale}px)`,
                cursor: 'pointer'
              }}
            >
              {messages.cms_add_element}
            </a>
          </OverlayTrigger>
        </div>
      )}

      {getAppendHtml()}
    </div>
  );

  return (
    <>
      <CustomScrollTrigger className="cms-overflow-fix" onEnter={() => setShown(true)} {...getCustomId()}>
        {parallaxData ? (
          <Parallax {...parallaxData}>
            {innerContent}
          </Parallax>
        ) : (
          innerContent
        )}
      </CustomScrollTrigger>
    </>
  );
};

export default CmsElementCol;
